<script lang="ts" module>
  import { KIND, SIZE } from "@/libs/ui/Button"

  export { KIND, SIZE }
</script>

<script lang="ts">
  import { Button } from "@/libs/ui/Button"
  import { onDestroy, type ComponentProps } from "svelte"

  let {
    accept = "image/*",
    onUpload,
    onClick,
    children,
    ...props
  }: {
    accept?: string
    onUpload: (file: File) => void
    onClick?: () => void
  } & Omit<ComponentProps<typeof Button>, "as" | "type"> = $props()

  let isProcessing = $state(false)
  let processingTimeout: ReturnType<typeof setTimeout> | undefined

  const clearProcessingTimeout = () => {
    if (!processingTimeout) return

    clearTimeout(processingTimeout)
    processingTimeout = undefined
  }

  onDestroy(clearProcessingTimeout)

  const handleInputClick = () => {
    // Set processing state to provide visual feedback
    isProcessing = true

    // Clear any existing timeout
    clearProcessingTimeout()

    // Reset processing state after a delay to handle cancel/dismiss scenarios
    processingTimeout = setTimeout(() => {
      isProcessing = false
    }, 1500)

    // For analytics purposes, we need to fire the onClick event when the file dialog actually opens
    onClick?.()
  }

  const onFileUpload = ({ currentTarget }: { currentTarget: HTMLInputElement }) => {
    const file = currentTarget.files?.[0]

    if (file) onUpload(file)

    // Clear the timeout since we have a definitive result
    clearProcessingTimeout()

    // Reset processing state after file selection/cancellation
    isProcessing = false

    currentTarget.value = "" // reset the input value to allow uploading the same file again, https://stackoverflow.com/a/60886920
  }
</script>

<Button
  as="label"
  {...props}
  type="button"
  class={[{ "pointer-events-none opacity-70": isProcessing }, props.class]}
  onclick={(e) =>
    // Force button to lose focus to prevent stuck active state
    e.currentTarget.blur()}
  ontouchend={(e) =>
    // Force remove focus on touch end to prevent stuck states
    e.currentTarget.blur()}
>
  {@render children?.()}
  <input
    class="absolute hidden"
    type="file"
    {accept}
    onclick={handleInputClick}
    oninput={onFileUpload}
    disabled={props.disabled}
    onblur={() => {
      // Add a small delay to handle the case where dialog closes without selection
      setTimeout(() => {
        if (isProcessing && processingTimeout) {
          clearProcessingTimeout()
          isProcessing = false
        }
      }, 100)
    }}
  />
</Button>

<style>
  /* Prevent stuck active states on mobile */
  :global(label:active) {
    -webkit-tap-highlight-color: transparent;
  }
</style>
